// eslint-disable-next-line no-unused-vars
import React from 'react';
import { useTranslation } from 'react-i18next';
import { func, object, array, bool } from 'prop-types';
import uniqueId from 'lodash/uniqueId';
import { useTheme } from '@emotion/react';
import styled from '@emotion/styled';
import { Checkbox, NumberInput } from '@carbon/react';
import { Range as Slider } from 'rc-slider';
import FilterListComponent from 'Components/Filters/FilterListComponent';

const StyledFilterGroup = styled.div`
  max-height: none;
  .options + hr {
    height: 2px;
    width: 100%;
    background-color: ${ props => props.theme.products.colors.border };
    outline: 0;
    border: 0;
  }
`;
const StyledPriceFilterGroup = styled(StyledFilterGroup)`
  overflow: visible !important;

  .sliderInput {
    width: 96%;
    margin: 5px auto 5px 2%;
    &.rc-slider-disabled {
      cursor: not-allowed;
    }

    .rc-slider-rail {
      height: 3px;
      box-sizing: border-box;
      background-color: ${ props => props.theme.colors.white };
      border: 1px solid ${ props => props.theme.products.colors.border };
      border-radius: 0;
    }

    .rc-slider-track {
      background-color: ${ props => props.theme.colors.darkBlue };
      height: 3px;
    }

    .rc-slider-handle {
      width: 10px;
      height: 10px;
      margin-top: -3px;
      border-radius: 0;
      border-color: ${ props => props.theme.colors.default };
      background-color: ${ props => props.theme.colors.white };
      box-sizing: border-box;
    }
  }
  .numberInput {
    display: flex;
    align-items: center;
    margin-top: 10px;

    > span {
      margin: 0 10px;
    }

    > div {
      input {
        min-width: 0;
        padding: 0 5px;
        border: 1px solid #CDCFD2 !important;
        color: ${ props => props.theme.colors.darkGray };
        font-size: 0.875em;
        height: 20px;
        text-align: center;
      }
    }
  }
`;

const PriceFilter = (
  {
    priceGroups,
    isInterval,
    selected,
    sliderOptions,
    onActivateInterval,
    onGroupChange,
    onIntervalAfterChange,
    onIntervalNumberChange,
    onIntervalChange,
    onToggle,
    open
  }
) => {
  const theme = useTheme();
  const { t } = useTranslation();
  return (
    <FilterListComponent
      showSearch={ false }
      onToggle={ onToggle }
      cx={ open ? 'open' : 'closed' }
      filterTitle={ t('filters.price') }
    >
      { priceGroups.length > 0 && (
        <>
          <div className="options">
            { priceGroups.map((priceGroup, idx) => {
              let label = `${ priceGroup[0] } - ${ priceGroup[1] }`;
              if (idx === priceGroups.length - 1) {
                label = `${t('common.over')} ${priceGroup[0]}`;
              }
              return (
                <div className="filterItem" key={ uniqueId(`priceGroup_${idx}`) }>
                  <Checkbox
                    id={ uniqueId('priceGroup') }
                    name={ `priceGroup${ idx }` }
                    labelText={ label }
                    checked={
                      !isInterval
                      && (
                        selected[0] === priceGroup[0]
                        && selected[1] === priceGroup[1]
                      )
                    }
                    onChange={ (_, { checked }) => onGroupChange(priceGroup)(checked) }
                  />
                </div>
              );
            }) }
          </div>
          <hr />
        </>
      ) }
      <StyledPriceFilterGroup theme={ theme } className="filterGroup">
        <div className="options">
          <div className="filterItem">
            <Checkbox
              id={ uniqueId('priceInterval') }
              name="priceInterval"
              labelText={ t('filters.priceInterval') }
              checked={ isInterval }
              onChange={ (_, { checked }) => onActivateInterval(checked) }
            />
          </div>
        </div>
        <Slider
          className="sliderInput"
          value={ [
            // eslint-disable-next-line no-restricted-globals
            isNaN(selected[0]) ? 0 : (selected[0] / sliderOptions.max) * 100,
            // eslint-disable-next-line no-restricted-globals
            isNaN(selected[1]) ? 100 : (selected[1] / sliderOptions.max) * 100
          ] }
          disabled={ !isInterval }
          onAfterChange={ onIntervalAfterChange }
          onChange={ onIntervalChange }
          count={ 2 }
        />
        <div className="numberInput">
          <NumberInput
            id="min_price"
            disabled={ !isInterval }
            allowEmpty
            light
            hideSteppers
            value={ selected[0] || sliderOptions.min }
            onChange={ e => onIntervalNumberChange(e.target.value, 'min') }
          />
          <span>-</span>
          <NumberInput
            id="max_price"
            allowEmpty
            light
            disabled={ !isInterval }
            hideSteppers
            value={ selected[1] || sliderOptions.max }
            onChange={ e => onIntervalNumberChange(e.target.value, 'max') }
          />
        </div>
      </StyledPriceFilterGroup>
    </FilterListComponent>
  );
};

PriceFilter.propTypes = {
  open: bool,
  onToggle: func,
  priceGroups: array,
  selected: array,
  isInterval: bool,
  sliderOptions: object,
  onActivateInterval: func,
  onGroupChange: func,
  onIntervalChange: func,
  onIntervalAfterChange: func,
  onIntervalNumberChange: func,
};
PriceFilter.displayName = 'PriceFilterComponent';

export default PriceFilter;
